import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Card,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { tokens } from "../../theme";
import { PageHeader } from "../../components/PageHeader";
import CommitDrawer from "../../components/CommitDrawer";
import { OwnerGate } from "../../components/supplier/OwnerGate";
import { describeError, useToast } from "../../components/toast";
import {
  foyerApi,
  type OwnerBranding,
  type OwnerShell,
} from "../../http/foyer";

/**
 * How a supplier is credited (`CONTENT-20`).
 *
 * ## 🚨 The colours are gone, and that was a product decision
 *
 * This page used to set a background, an ink and an accent, which the shell
 * then wore — `CONTENT-4` had ruled the supplier surface WAS the white-label
 * storefront, so per-owner theming was a first-commit requirement. Sigge
 * withdrew that: *"I never asked for my brand - we can drop that entirely"*.
 * The content-owner pages are the Nenda Portal, full stop.
 *
 * So the theming half is deleted rather than hidden. A colour picker whose
 * output nothing renders is worse than no picker: a partner sets a brand, sees
 * no change, and sets it again.
 *
 * What survives is `identity`, which was never theming — a display name, a
 * website, credits and a copyright line are attribution that belongs to the
 * supplier whatever the page is painted with.
 *
 * ⚠️ `brand` is still on the model and the server still accepts it. Left
 * deliberately: dropping a stored field is a migration, and a studio that
 * already set colours should not have them silently erased by a redeploy. This
 * page simply no longer writes them.
 *
 * ## Owner-editable, operator read-only
 *
 * The staff route refuses these fields. An operator quietly correcting a
 * studio's copyright line is a divergence the studio cannot see.
 *
 * ## Stored, and deliberately not on a TV
 *
 * Nothing in `src/tv` renders content attribution today: which surface, and
 * which corner — two logos already want the same one and nothing arbitrates.
 * Storing what a partner tells us is honest; rendering it somewhere picked on
 * the day is not. That is its own ticket.
 */

interface ShellContext {
  shell: OwnerShell;
}

type Identity = OwnerBranding["identity"];

export default function SupplierBrandPage() {
  useOutletContext<ShellContext>();
  const toast = useToast();
  const { t } = useTranslation(["supplier", "common"]);
  const [branding, setBranding] = useState<OwnerBranding | null>(null);
  // Load failures only: a failed SAVE is a toast and never replaces the page.
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    let live = true;
    foyerApi
      .ownerBranding()
      .then((b) => live && setBranding(b))
      .catch(
        (e) => live && setError(e instanceof Error ? e.message : String(e))
      );
    return () => {
      live = false;
    };
  }, []);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!branding) return <CircularProgress size={24} />;

  const rows: Array<[string, string | undefined]> = [
    [t("supplierBrandPage.fields.displayName"), branding.identity.displayName],
    [t("supplierBrandPage.fields.website"), branding.identity.websiteUrl],
    [t("supplierBrandPage.fields.credits"), branding.identity.creditsText],
    [t("supplierBrandPage.fields.copyright"), branding.identity.copyrightText],
  ];

  async function save(identity: Identity): Promise<boolean> {
    try {
      // 🚨 `identity` only. Sending `brand` back would re-write colours this
      // page no longer shows, from state it no longer edits.
      setBranding(await foyerApi.saveOwnerBranding({ identity }));
      toast.success(t("supplierBrandPage.saved"));
      return true;
    } catch (e) {
      toast.error(describeError(t, "saveCreditDetails", e));
      return false;
    }
  }

  return (
    <Stack spacing={3} sx={{ maxWidth: 640 }}>
      <PageHeader
        eyebrow={t("supplierBrandPage.eyebrow")}
        title={t("supplierBrandPage.title")}
        subtitle={t("supplierBrandPage.subtitle")}
        actions={
          <OwnerGate resource="content-owners/brand" level="edit">
            <Button
              variant="contained"
              onClick={() => setEditing(true)}
              data-testid="identity-edit"
            >
              {t("common:buttons.edit")}
            </Button>
          </OwnerGate>
        }
      />

      <Card variant="outlined" sx={{ p: 2.5 }}>
        <Stack spacing={1.5}>
          {rows.map(([label, value]) => (
            <Stack key={label} spacing={0.25}>
              <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                {label}
              </Typography>
              <Typography variant="body2">
                {value || t("common:labels.emptyValue")}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Card>

      {editing && (
        <IdentityDrawer
          initial={branding.identity}
          onClose={() => setEditing(false)}
          onSave={save}
        />
      )}
    </Stack>
  );
}

function IdentityDrawer({
  initial,
  onClose,
  onSave,
}: {
  initial: Identity;
  onClose: () => void;
  onSave: (identity: Identity) => Promise<boolean>;
}) {
  const { t } = useTranslation("supplier");
  const [draft, setDraft] = useState<Identity>(initial);
  const field = (label: string, key: keyof Identity, helper?: string) => (
    <TextField
      label={label}
      size="small"
      value={draft[key] ?? ""}
      onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
      helperText={helper}
      fullWidth
    />
  );
  return (
    <CommitDrawer
      title={t("supplierBrandPage.title")}
      onClose={onClose}
      onSave={() => onSave(draft)}
      data-testid="identity-drawer"
    >
      <Stack spacing={2}>
        {field(t("supplierBrandPage.fields.displayName"), "displayName")}
        {field(t("supplierBrandPage.fields.website"), "websiteUrl")}
        {field(
          t("supplierBrandPage.fields.credits"),
          "creditsText",
          t("supplierBrandPage.creditsHelp")
        )}
        {field(
          t("supplierBrandPage.fields.copyright"),
          "copyrightText",
          t("supplierBrandPage.copyrightHelp")
        )}
      </Stack>
    </CommitDrawer>
  );
}
